import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ErroDeDado, ErroDeRede, carregarCatalogo, carregarRebanho, simular } from '../nucleo/api.js';
import {
  NOME_DA_ESPECIE,
  agruparPorEspecie,
  escalaEscolhida,
  escalasPossiveis,
  juntarEspecies,
  oQueFalta,
} from '../nucleo/especies.js';
import { guardar, ler } from '../nucleo/armazenamento.js';
import { enxuto, expressivo, lerNumero, plural } from '../nucleo/numero.js';
import { Recusa } from '../componentes/Recusa.jsx';
import { Resultado } from '../componentes/Resultado.jsx';

/**
 * A primeira tela: duas perguntas e uma resposta.
 *
 * O critério que mandou nesta tela é tempo. Quem chega aqui está decidindo se
 * vale a pena ler o resto, e a resposta precisa caber em menos de um minuto —
 * escolher o animal, digitar quantos, ver o número. Tudo o que não cabe nesse
 * minuto fica recolhido: a análise própria, a conta passo a passo, as fontes.
 *
 * Três decisões que parecem de layout e são de produto:
 *
 * **A pendência aparece na mesma lista que o dado.** As cinco categorias sem
 * análise continuam no seletor, marcadas. Esconder o que falta deixaria a
 * lista mais bonita e ensinaria o consultor a não procurar o resto — e a
 * lacuna é do setor, não deste app.
 *
 * **O impedimento é dito antes do envio, não depois.** Assim que a categoria é
 * escolhida, a tela já sabe (pelas duas rotas carregadas juntas) se vai dar
 * para calcular. Mandar o produtor digitar, esperar e receber um 422 tem a
 * mesma informação e chega tarde.
 *
 * **A escala é oferecida conforme o que existe.** Cama de aviário não aceita
 * "quantos frangos": cama se mede por lote e por metro quadrado. Oferecer o
 * campo ali seria prometer uma conta que a própria API recusa.
 */

const CHAVE_DO_RASCUNHO = 'rascunho:simulador';

const ROTULO_DA_ESCALA = {
  cabecas: 'Por cabeças',
  massa: 'Por toneladas de material',
};

function rascunhoGuardado() {
  const r = ler(CHAVE_DO_RASCUNHO);
  if (!r || typeof r !== 'object') return null;
  return r;
}

/** O item do seletor, com a marca de pendência quando é o caso. */
function textoDaOpcao(item) {
  if (item.podeCalcular) return item.rotulo;
  return `${item.rotulo} — sem análise ainda`;
}

function Carregando() {
  return (
    <p className="secundario" role="status">
      Carregando as categorias…
    </p>
  );
}

function SemCatalogo({ erro, aoTentarDeNovo }) {
  const offline = erro instanceof ErroDeRede && erro.offline;
  return (
    <section className="cartao" role="alert">
      <h2 className="recusa__titulo">
        {offline ? 'Sem conexão e sem cópia guardada' : 'Não consegui carregar as categorias'}
      </h2>
      <p>
        {offline
          ? 'Abra o app uma vez com internet: depois disso a lista fica guardada no aparelho e a consulta funciona sem sinal.'
          : erro?.message}
      </p>
      <button type="button" className="botao" onClick={aoTentarDeNovo}>
        Tentar de novo
      </button>
    </section>
  );
}

export function Simulador() {
  const [itens, setItens] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [erroDaLista, setErroDaLista] = useState(null);
  const [cacheDe, setCacheDe] = useState(null);

  const [chave, setChave] = useState('');
  const [escala, setEscala] = useState('cabecas');
  const [quantidade, setQuantidade] = useState('');
  // Opcional de propósito, e o padrão erra para o lado que não
  // lisonjeia: vazio vale zero, tudo vira excedente, e excedente paga
  // 70% da tarifa. Medido nos mesmos 50 suínos: R$ 2.039 por ano sem a
  // conta de luz contra R$ 2.913 com ela. Se o padrão fosse o contrário,
  // quem não respondesse veria o número maior.
  const [consumo, setConsumo] = useState('');
  const [tentouEnviar, setTentouEnviar] = useState(false);

  const [calculando, setCalculando] = useState(false);
  const [envelope, setEnvelope] = useState(null);
  const [erroDoCalculo, setErroDoCalculo] = useState(null);

  const areaDoResultado = useRef(null);

  const buscarListas = useCallback(async () => {
    setCarregandoLista(true);
    setErroDaLista(null);
    try {
      const [rebanho, catalogo] = await Promise.all([carregarRebanho(), carregarCatalogo()]);
      setItens(juntarEspecies(rebanho.itens ?? [], catalogo.itens ?? []));
      setCacheDe(rebanho.deCache || catalogo.deCache ? (rebanho.cacheDe ?? catalogo.cacheDe) : null);
    } catch (erro) {
      setErroDaLista(erro);
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    buscarListas();
  }, [buscarListas]);

  // O rascunho só é aplicado depois que a lista chega: restaurar uma chave que
  // não existe mais no catálogo deixaria o seletor em branco sem explicação.
  useEffect(() => {
    if (!itens.length) return;
    const r = rascunhoGuardado();
    if (!r) return;
    if (r.chave && itens.some((i) => i.chave === r.chave)) {
      setChave(r.chave);
      if (r.escala) setEscala(r.escala);
      if (r.quantidade) setQuantidade(String(r.quantidade));
      if (r.consumo) setConsumo(String(r.consumo));
    }
  }, [itens]);

  const item = useMemo(() => itens.find((i) => i.chave === chave) ?? null, [itens, chave]);
  const escalas = useMemo(() => escalasPossiveis(item), [item]);
  const grupos = useMemo(() => agruparPorEspecie(itens), [itens]);

  // A correção da escala é derivada, e mora no núcleo porque é ela que
  // sustenta a invariante "categoria que pode calcular não cai em escala com
  // pendência" — a mesma que torna `faltas.length` redundante no
  // impedimento abaixo. Testada lá nas quatro combinações.
  const escalaAtual = escalaEscolhida(item, escala);
  const faltas = useMemo(() => oQueFalta(item, escalaAtual), [item, escalaAtual]);

  useEffect(() => {
    if (!chave) return;
    guardar(CHAVE_DO_RASCUNHO, { chave, escala, quantidade, consumo });
  }, [chave, escala, quantidade, consumo]);

  useEffect(() => {
    if (!envelope) return;
    const alvo = areaDoResultado.current;
    if (alvo && typeof alvo.scrollIntoView === 'function') {
      alvo.scrollIntoView({ block: 'start' });
    }
  }, [envelope]);

  const valor = lerNumero(quantidade);
  const quantidadeValida = valor !== null && valor > 0;

  /**
   * A conta de luz: em branco é válido, escrito errado não é.
   *
   * `1_000_000` não é limiar meu: é o `le` do campo na API. Deixar
   * passar daria 422 na cara de quem digitou um número — a tela diria
   * "não consegui calcular agora" para um erro que ela sabia prever.
   */
  const consumoMes = lerNumero(consumo);
  const consumoEscrito = consumo.trim() !== '';
  const consumoValido =
    !consumoEscrito ||
    (consumoMes !== null && consumoMes >= 0 && consumoMes <= 1_000_000);
  /**
   * O que nenhuma digitação resolve — e só isso desabilita o botão.
   *
   * Sem categoria escolhida ou sem análise a conta não existe, e oferecer o
   * botão seria mentir sobre o que a tela sabe fazer.
   *
   * `faltas.length` aqui é cinto sobre suspensório, e é honesto dizer: dado
   * `escalaEscolhida`, categoria com análise nunca cai numa escala com
   * pendência, então este termo nunca é o que decide. Ficou porque a
   * invariante é do núcleo e pode mudar sem ninguém olhar esta linha; está
   * medido que removê-lo hoje não derruba teste nenhum.
   */
  const impedimentoMaterial =
    !item || !item.podeCalcular || faltas.length > 0;

  /**
   * O que a requisicao precisa para sair. Diferente do de cima de proposito:
   * quantidade vazia e conta de luz escrita errada sao erro de DIGITACAO, e
   * para esses o botao continua ativo.
   *
   * Medido antes desta separacao: as duas mensagens de erro de campo eram
   * codigo morto. `tentouEnviar` so viravel verdadeiro no `onSubmit`, e o
   * submit estava bloqueado justamente nos casos em que a mensagem deveria
   * aparecer -- clique e Enter, os dois. Quem digitasse "mil e duzentos" via
   * o botao apagar e nenhuma frase dizendo o porque. Botao desabilitado
   * tambem nao se anuncia a leitor de tela: ele nao tem o que ler.
   */
  const podeEnviar =
    !impedimentoMaterial && quantidadeValida && consumoValido;

  async function aoEnviar(evento) {
    evento.preventDefault();
    setTentouEnviar(true);
    if (!podeEnviar || calculando) return;

    setCalculando(true);
    setErroDoCalculo(null);
    setEnvelope(null);
    try {
      const entrada = { especie: chave, nome: item.rotuloDoMaterial || item.rotulo };
      if (escalaAtual === 'cabecas') entrada.cabecas = valor;
      else entrada.massa_t_dia = valor;
      // Campo ausente e campo zerado dizem a mesma coisa para a API (o
      // `default=0.0`), e é melhor não mandar: assim o corpo da
      // requisição guarda a diferença entre "não perguntei" e "o
      // produtor respondeu zero", que é o que vai no log.
      if (consumoEscrito && consumoMes !== null && consumoMes > 0) {
        entrada.consumo_granja_kwh_mes = consumoMes;
      }

      setEnvelope(await simular(entrada));
    } catch (erro) {
      setErroDoCalculo(erro);
    } finally {
      setCalculando(false);
    }
  }

  function trocarDeCategoria(novaChave) {
    setChave(novaChave);
    setEnvelope(null);
    setErroDoCalculo(null);
    setTentouEnviar(false);
  }

  if (carregandoLista && !itens.length) return <Carregando />;
  if (erroDaLista && !itens.length) {
    return <SemCatalogo erro={erroDaLista} aoTentarDeNovo={buscarListas} />;
  }

  /**
   * A quantidade digitada, relida para o produtor conferir.
   *
   * Cabeça é contável e tonelada não é: `expressivo(50)` devolvia
   * "Estimativa para 50,0 animais", e meio porco não existe. Já "15,5
   * toneladas por dia" é legítimo e tem de manter a casa decimal. É a mesma
   * distinção que o motor faz na frase em Python (`contagem:animal:animais`
   * contra `massa_t`), e as duas pontas precisam arredondar igual — senão o
   * que o produtor digita aparece de um jeito aqui e de outro no resultado.
   */
  const quantidadeLida =
    escalaAtual === 'cabecas'
      ? plural(valor, 'animal', 'animais')
      : `${expressivo(valor)} ${Math.abs(valor) === 1 ? 'tonelada' : 'toneladas'} por dia`;

  return (
    <div className="simulador empilha-largo">
      <header className="simulador__abertura">
        <h1 className="simulador__titulo">Quanto o dejeto da sua criação vale em energia?</h1>
        <p className="simulador__chamada">
          Responda duas coisas e veja a estimativa. Sem cadastro, e a conta fica
          aberta para você conferir. A terceira pergunta é opcional e deixa o
          número mais justo.
        </p>
      </header>

      {cacheDe ? (
        <p className="aviso-de-cache" role="status">
          Você está vendo a lista guardada no aparelho
          {cacheDe ? ` desde ${new Date(cacheDe).toLocaleDateString('pt-BR')}` : ''}. Com
          internet ela se atualiza sozinha.
        </p>
      ) : null}

      <form className="cartao empilha" onSubmit={aoEnviar} noValidate>
        <div className="campo">
          <label className="campo__rotulo" htmlFor="categoria">
            Que animal você cria?
          </label>
          <select
            id="categoria"
            className="campo__selecao"
            value={chave}
            onChange={(e) => trocarDeCategoria(e.target.value)}
          >
            <option value="">Escolha…</option>
            {grupos.map(([especie, doGrupo]) => (
              <optgroup key={especie} label={NOME_DA_ESPECIE[especie] ?? especie}>
                {doGrupo.map((i) => (
                  <option key={i.chave} value={i.chave}>
                    {textoDaOpcao(i)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {item?.rotuloDoMaterial ? (
            <p className="campo__dica">O material é {item.rotuloDoMaterial}.</p>
          ) : null}
        </div>

        {item && faltas.length ? (
          <div className="pendencia" role="status">
            <span className="selo selo--catalogo">Falta medir</span>
            <ul className="pendencia__lista">
              {faltas.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            {item.analogo ? (
              <button
                type="button"
                className="botao botao--secundario"
                onClick={() => trocarDeCategoria(item.analogo)}
              >
                Estimar pela categoria medida mais próxima
              </button>
            ) : null}
          </div>
        ) : null}

        {item && escalas.length > 1 ? (
          <div className="escala" role="group" aria-label="Como informar a quantidade">
            {escalas.map((e) => (
              <button
                key={e}
                type="button"
                className={`escala__opcao ${escalaAtual === e ? 'escala__opcao--ativa' : ''}`}
                aria-pressed={escalaAtual === e}
                onClick={() => setEscala(e)}
              >
                {ROTULO_DA_ESCALA[e]}
              </button>
            ))}
          </div>
        ) : null}

        {item && item.podeCalcular ? (
          <div className={`campo ${tentouEnviar && !quantidadeValida ? 'campo--erro' : ''}`}>
            <label className="campo__rotulo" htmlFor="quantidade">
              {escalaAtual === 'cabecas'
                ? 'Quantos animais você tem?'
                : 'Quantas toneladas de material por dia?'}
            </label>
            <input
              id="quantidade"
              className="campo__entrada"
              inputMode="decimal"
              autoComplete="off"
              placeholder={escalaAtual === 'cabecas' ? 'ex.: 2000' : 'ex.: 15'}
              value={quantidade}
              onChange={(e) => setQuantidade(e.target.value)}
              aria-describedby="dica-quantidade"
            />
            <p className="campo__dica" id="dica-quantidade">
              {escalaAtual === 'cabecas' && item.dejetoCabDia !== null ? (
                <>
                  Pela tabela, cada {item.rotulo.toLowerCase()} gera cerca de{' '}
                  {expressivo(item.dejetoCabDia)} {item.forma === 'chorume' ? 'litros' : 'kg'} por
                  dia
                  {item.faixaDoDejeto
                    ? ` (a literatura dá de ${enxuto(item.faixaDoDejeto[0])} a ${enxuto(
                        item.faixaDoDejeto[1],
                      )})`
                    : ''}
                  .
                </>
              ) : (
                <>Informe a massa do material como ele sai, úmido.</>
              )}
            </p>
            {escalaAtual === 'cabecas' && item.incluiProgenie ? (
              <p className="campo__dica campo__dica--atencao">
                Este número já inclui a cria. Somar os leitões em separado contaria o
                mesmo dejeto duas vezes.
              </p>
            ) : null}
            {tentouEnviar && !quantidadeValida ? (
              <p className="campo__dica campo__dica--erro" role="alert">
                Escreva um número maior que zero — por exemplo 2000.
              </p>
            ) : null}
          </div>
        ) : null}

        {item && item.podeCalcular ? (
          <div className={`campo ${tentouEnviar && !consumoValido ? 'campo--erro' : ''}`}>
            <label className="campo__rotulo" htmlFor="consumo">
              Quanto de luz a propriedade gasta por mês?{' '}
              <span className="campo__opcional">opcional</span>
            </label>
            <input
              id="consumo"
              className="campo__entrada"
              inputMode="numeric"
              autoComplete="off"
              placeholder="o kWh que vem na conta"
              value={consumo}
              onChange={(e) => setConsumo(e.target.value)}
              aria-describedby="dica-consumo"
            />
            <p className="campo__dica" id="dica-consumo">
              É o número de kWh da sua conta de luz. Com ele, a conta separa a
              energia que deixa de ser comprada da que vai para a rede — e as
              duas valem dinheiro diferente. Em branco, consideramos que tudo
              vai para a rede, que é a leitura mais conservadora.
            </p>
            {tentouEnviar && !consumoValido ? (
              <p className="campo__dica campo__dica--erro" role="alert">
                Escreva só o número de kWh — por exemplo 1200 — ou deixe em branco.
              </p>
            ) : null}
          </div>
        ) : null}

        <button
          type="submit"
          className="botao botao--largo"
          disabled={impedimentoMaterial || calculando}
        >
          {calculando ? 'Calculando…' : 'Ver quanto vale'}
        </button>

        {item && item.podeCalcular && quantidadeValida ? (
          <p className="terciario">
            Estimativa para {quantidadeLida}.
          </p>
        ) : null}
      </form>

      <div ref={areaDoResultado} aria-live="polite">
        {erroDoCalculo instanceof ErroDeDado ? (
          <Recusa erro={erroDoCalculo} onUsarAnalogo={trocarDeCategoria} />
        ) : null}

        {erroDoCalculo instanceof ErroDeRede ? (
          <section className="cartao" role="alert">
            <h2 className="recusa__titulo">
              {erroDoCalculo.offline ? 'Você está sem conexão' : 'Não consegui calcular agora'}
            </h2>
            <p>
              {erroDoCalculo.offline
                ? 'A conta ainda precisa do servidor. Assim que o sinal voltar, toque de novo em "Ver quanto vale".'
                : erroDoCalculo.message}
            </p>
          </section>
        ) : null}

        {envelope ? <Resultado envelope={envelope} /> : null}
      </div>
    </div>
  );
}
